import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { orderBy, QueryCompositeFilterConstraint } from "firebase/firestore";
import { FirestoreService } from "../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../shared/api/firebase/references";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { createCompositeFilterForMedicalPaTables } from "../../../utils/createCompositeFilterForTables";
import { logDataToConsole } from "../../../utils/customLogger";

interface MedicalPaOrdersState {
  podMedicalPaOrders: MedicalPaOrder[] | null;
  loading: boolean;
  error: string | null;
  lastVisible: any;
  showinLineLoader: boolean;
  totalDocuments: number;
  currentlyLoadedCount: number;
}

const initialState: MedicalPaOrdersState = {
  podMedicalPaOrders: null,
  loading: false,
  error: null,
  lastVisible: null,
  showinLineLoader: false,
  totalDocuments: 0,
  currentlyLoadedCount: 0,
};

const podMedicalPaOrderSlice = createSlice({
  name: "podMedicalPaOrders",
  initialState,
  reducers: {
    setpodMedicalPaOrders: (state, action: PayloadAction<MedicalPaOrder[]>) => {
      state.podMedicalPaOrders = action.payload;
      state.currentlyLoadedCount = action.payload.length;
      state.loading = false;
      state.error = null;
    },
    appendpodMedicalPaOrders: (
      state,
      action: PayloadAction<MedicalPaOrder[]>,
    ) => {
      state.podMedicalPaOrders = state.podMedicalPaOrders
        ? [...state.podMedicalPaOrders, ...action.payload]
        : action.payload;
      state.currentlyLoadedCount = state.podMedicalPaOrders.length;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.podMedicalPaOrders = null;
      state.loading = false;
    },
    setLastVisible: (state, action: PayloadAction<any>) => {
      state.lastVisible = action.payload;
    },
    setTotalDocuments: (state, action: PayloadAction<number>) => {
      state.totalDocuments = action.payload;
    },
    resetPagination: (state) => {
      state.totalDocuments = 0;
      state.lastVisible = null;
      state.currentlyLoadedCount = 0;
      state.podMedicalPaOrders = null;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
  },
});

export const {
  setpodMedicalPaOrders,
  appendpodMedicalPaOrders,
  setLoading,
  setError,
  setLastVisible,
  setTotalDocuments,
  resetPagination,
  setinLineLoader,
} = podMedicalPaOrderSlice.actions;

export const fetchTotalDocumentsCount =
  (
    facilityId: string,
    providerId: string,
    searchTerm?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch) => {
    try {
      const allDocs =
        await FirestoreService.getAllDocumentCountByCompositeFilter(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId,
            MedicalPaOrdersAuthStatus.POD,
            searchTerm,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
        );

      dispatch(setTotalDocuments(allDocs));
    } catch (error) {
      logDataToConsole("Error fetching total document count", error);
      dispatch(setTotalDocuments(0));
      return 0;
    }
  };

export const fetchPodMedicalPaOrdersAfterSearch =
  (
    facilityId: string,
    providerId: string,
    searchTerm?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch, getState) => {
    const { podMedicalPaOrders } = getState();
    if (podMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    try {
      const data =
        await FirestoreService.fetchQueryCollectionByCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId,
            MedicalPaOrdersAuthStatus.POD,
            searchTerm,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
          [orderBy("created_at", "desc")],
        );

      dispatch(setpodMedicalPaOrders(data as MedicalPaOrder[]));
      dispatch(
        fetchTotalDocumentsCount(
          facilityId,
          providerId,
          searchTerm,
          isAdmin ?? false,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchPodMedicalPaOrders =
  (
    facilityId: string,
    pageSize: number,
    providerId: string,
    isAdmin: boolean,
  ) =>
  async (dispatch, getState) => {
    const { podMedicalPaOrders } = getState();
    if (podMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    try {
      FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(setpodMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(fetchTotalDocumentsCount(facilityId, providerId));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        undefined,
        createCompositeFilterForMedicalPaTables(
          facilityId,
          providerId,
          MedicalPaOrdersAuthStatus.POD,
          undefined,
          isAdmin,
        ) as QueryCompositeFilterConstraint,
        [orderBy("created_at", "desc")],
      );
    } catch (error) {
      logDataToConsole("Error in fetchPodMedicalPaOrders", error);
      dispatch(setError(error as string));
    }
  };

export const fetchMorePodMedicalPaOrders =
  (
    facilityId: string,
    pageSize: number,
    providerId?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch, getState) => {
    const { podMedicalPaOrders } = getState();

    if (
      podMedicalPaOrders.loading ||
      podMedicalPaOrders.showinLineLoader ||
      podMedicalPaOrders.currentlyLoadedCount ===
        podMedicalPaOrders.totalDocuments
    ) {
      return;
    }

    dispatch(setinLineLoader(true));

    try {
      const unsubscribe =
        FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          pageSize,
          ({ docs, lastVisible }) => {
            dispatch(appendpodMedicalPaOrders(docs));
            dispatch(setLastVisible(lastVisible));
            dispatch(setinLineLoader(false));
          },
          (error) => {
            dispatch(setError(error.message));
            dispatch(setinLineLoader(false));
          },
          podMedicalPaOrders.lastVisible,
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId ?? "",
            MedicalPaOrdersAuthStatus.POD,
            undefined,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
          [orderBy("created_at", "desc")],
        );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setinLineLoader(false));
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { podMedicalPaOrders } = state;

  return (
    podMedicalPaOrders.currentlyLoadedCount <
      podMedicalPaOrders.totalDocuments &&
    endIndex === podMedicalPaOrders.currentlyLoadedCount - 1
  );
};

export default podMedicalPaOrderSlice.reducer;
